import { FileBarChart2, FileCheck2, MessageSquareText, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { apiOuvrir } from '@/api/client';
import { useActionsCommentairesCloture, useActionsRapports, useDestinatairesRapport, useEnvoisRapport, usePerimetresRapport, useRapports, type GenererRapportSaisie } from '@/api/hooks';
import { LIBELLE_STATUT_ENVOI, type ResumeRapport } from '@/api/types';
import { useSession } from '@/auth/session';
import { BoutonExcel, BoutonPdf, Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateHeureFr, montant, pct } from '@/lib/utils';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function DialogueGeneration({ ouvert, onOpenChange }: { ouvert: boolean; onOpenChange: (o: boolean) => void }) {
  const perimetres = usePerimetresRapport();
  const actions = useActionsRapports();
  const maintenant = new Date();
  const [v, setV] = useState<GenererRapportSaisie>({ exercice: maintenant.getFullYear(), type: 'CUMUL', mois: maintenant.getMonth() + 1, trimestre: Math.ceil((maintenant.getMonth() + 1) / 3), structureCode: '', comparaison: 'AUCUNE' });
  const maj = (patch: Partial<GenererRapportSaisie>) => setV({ ...v, ...patch });
  const generer = async () => {
    try {
      const choix = v.structureCode ?? '';
      const [structureCode, serviceCode] = choix.includes('/') ? choix.split('/') : [choix, undefined];
      const r = await actions.generer.mutateAsync({ exercice: v.exercice, type: v.type, mois: v.type === 'MOIS' ? v.mois : undefined, trimestre: v.type === 'TRIMESTRE' ? v.trimestre : undefined, structureCode: structureCode || undefined, serviceCode: serviceCode || undefined, comparaison: v.comparaison });
      toast.success(`Rapport ${r.numero} généré et archivé (${r.pages} pages).`);
      onOpenChange(false);
      await apiOuvrir(`/rapports/${r.id}/pdf`, r.nomFichier);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Génération impossible.');
    }
  };
  const fonds = perimetres.data?.fonds ?? false;
  useEffect(() => {
    if (perimetres.data && !perimetres.data.fonds && !v.structureCode) {
      const premier = perimetres.data.structures[0]?.code ?? perimetres.data.services[0]?.perimetre ?? '';
      if (premier) setV((x) => ({ ...x, structureCode: premier }));
    }
  }, [perimetres.data, v.structureCode]);
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Générer un rapport d'exécution</DialogTitle>
          <DialogDescription>Période, périmètre et comparaison du rapport ; le PDF est archivé dans la bibliothèque, numéroté et horodaté, puis ouvert dans un nouvel onglet.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="exercice">Exercice</Label>
            <Input id="exercice" type="number" min={2020} max={2100} value={v.exercice} onChange={(e) => maj({ exercice: Number(e.target.value) })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="type">Période</Label>
            <SelectNatif id="type" value={v.type} onChange={(e) => maj({ type: e.target.value as GenererRapportSaisie['type'] })}>
              <option value="CUMUL">Cumul annuel à la date du jour</option>
              <option value="MOIS">Mois</option>
              <option value="TRIMESTRE">Trimestre</option>
            </SelectNatif>
          </div>
          {v.type === 'MOIS' && (
            <div className="space-y-1.5">
              <Label htmlFor="mois">Mois</Label>
              <SelectNatif id="mois" value={v.mois} onChange={(e) => maj({ mois: Number(e.target.value) })}>
                {MOIS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </SelectNatif>
            </div>
          )}
          {v.type === 'TRIMESTRE' && (
            <div className="space-y-1.5">
              <Label htmlFor="trimestre">Trimestre</Label>
              <SelectNatif id="trimestre" value={v.trimestre} onChange={(e) => maj({ trimestre: Number(e.target.value) })}>
                {[1, 2, 3, 4].map((t) => (
                  <option key={t} value={t}>
                    T{t}
                  </option>
                ))}
              </SelectNatif>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="perimetre">Périmètre</Label>
            <SelectNatif id="perimetre" value={v.structureCode} onChange={(e) => maj({ structureCode: e.target.value })}>
              {fonds && <option value="">Tout le Fonds</option>}
              {(perimetres.data?.structures ?? []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
              {(perimetres.data?.services ?? []).map((s) => (
                <option key={s.perimetre} value={s.perimetre}>
                  {s.perimetre} · {s.libelle} (service)
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="comparaison">Comparaison</Label>
            <SelectNatif id="comparaison" value={v.comparaison} onChange={(e) => maj({ comparaison: e.target.value as 'AUCUNE' | 'N-1' | 'T-1' })}>
              <option value="AUCUNE">Aucune</option>
              <option value="N-1">Même période de l'exercice précédent (N-1)</option>
              <option value="T-1">Période précédente (T/T-1, mois précédent)</option>
            </SelectNatif>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={generer} disabled={actions.generer.isPending || perimetres.isLoading}>
            {actions.generer.isPending ? 'Génération…' : 'Générer le rapport'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ADRESSE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Envoi ou renvoi d'un rapport par courriel (§ 11.3) : destinataires du périmètre cochables, adresses supplémentaires, message, historique des envois. */
function DialogueEnvoi({ rapport, onOpenChange }: { rapport: ResumeRapport; onOpenChange: (o: boolean) => void }) {
  const proposes = useDestinatairesRapport(rapport.id);
  const envois = useEnvoisRapport(rapport.id);
  const actions = useActionsRapports();
  const [exclus, setExclus] = useState<string[]>([]);
  const [autres, setAutres] = useState('');
  const [message, setMessage] = useState('');
  const liste = proposes.data?.destinataires ?? [];
  const retenus = liste.filter((d) => !exclus.includes(d.email)).map((d) => d.email);
  const supplementaires = autres.split(/[\s,;]+/).map((a) => a.trim()).filter(Boolean);
  const invalides = supplementaires.filter((a) => !ADRESSE.test(a));
  const destinataires = [...new Set([...retenus, ...supplementaires])];
  const modifie = exclus.length > 0 || supplementaires.length > 0;
  const envoyer = async () => {
    try {
      const r = await actions.envoyer.mutateAsync({ id: rapport.id, destinataires: modifie ? destinataires : undefined, message: message.trim() || undefined });
      if (r.statut === 'ENVOYE') toast.success(`Rapport ${rapport.numero} envoyé à ${r.destinataires.length} destinataire(s).`);
      else if (r.statut === 'JOURNALISE') toast.warning(`Aucun transport de courriel sur cet environnement : l'envoi à ${r.destinataires.length} destinataire(s) a été journalisé, non expédié.`);
      else toast.error(`Envoi en échec : ${r.detail ?? 'erreur du transport'}.`);
      setAutres('');
      setMessage('');
      setExclus([]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Envoi impossible.');
    }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {rapport.nombreEnvois ? 'Renvoyer' : 'Envoyer'} le rapport {rapport.numero}
          </DialogTitle>
          <DialogDescription>
            {rapport.periode} · {rapport.libellePerimetre}. Le PDF est joint au courriel, envoyé depuis l'adresse institutionnelle, avec le lien vers la bibliothèque ; l'envoi est tracé et le rapport figé.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="mb-1 text-sm font-medium">
              Destinataires {proposes.data && (proposes.data.listeDefinie ? <Badge variant="secondary">liste du périmètre</Badge> : <Badge variant="outline">par défaut (aucune liste définie)</Badge>)}
            </div>
            {proposes.isLoading ? (
              <Chargement />
            ) : liste.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun destinataire pour ce périmètre : indiquez des adresses ci-dessous ou définissez une liste dans Administration › Destinataires des rapports.</p>
            ) : (
              <ul className="space-y-1">
                {liste.map((d) => (
                  <li key={d.email}>
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={!exclus.includes(d.email)} onCheckedChange={(c) => setExclus(c === true ? exclus.filter((e) => e !== d.email) : [...exclus, d.email])} />
                      <span>{d.nom}</span>
                      <span className="text-muted-foreground">{d.email}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="autres">Adresses supplémentaires (séparées par des virgules)</Label>
            <Input id="autres" value={autres} onChange={(e) => setAutres(e.target.value)} placeholder="audit.interne@fonarev.cd, cabinet@ministere.gouv.cd" />
            {invalides.length > 0 && <p className="text-xs text-red-700">Adresse(s) invalide(s) : {invalides.join(', ')}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="message">Message (facultatif)</Label>
            <Textarea id="message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} placeholder="Texte ajouté au corps du courriel, après les indicateurs clés." />
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">Historique des envois</div>
            {envois.isLoading ? (
              <Chargement />
            ) : !envois.data?.length ? (
              <p className="text-sm text-muted-foreground">Ce rapport n'a pas encore été envoyé.</p>
            ) : (
              <ul className="divide-y rounded-md border text-sm">
                {envois.data.map((e) => (
                  <li key={e.id} className="space-y-0.5 px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={e.statut === 'ENVOYE' ? 'default' : e.statut === 'ECHEC' ? 'destructive' : 'outline'}>{LIBELLE_STATUT_ENVOI[e.statut]}</Badge>
                      <span>{dateHeureFr(e.createdAt)}</span>
                      <span className="text-muted-foreground">par {e.envoyeParNom}</span>
                      {e.automatique && <Badge variant="outline">automatique</Badge>}
                      {!e.pieceJointe && <Badge variant="outline">sans pièce jointe</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {e.destinataires.filter((d) => !d.copie).map((d) => `${d.nom} <${d.email}>`).join(', ')}
                      {e.destinataires.some((d) => d.copie) && <div>En copie : {e.destinataires.filter((d) => d.copie).map((d) => d.nom).join(', ')}</div>}
                    </div>
                    {e.detail && <div className={cn('text-xs', e.statut === 'ECHEC' ? 'text-red-700' : 'text-muted-foreground')}>{e.detail}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={envoyer} disabled={actions.envoyer.isPending || destinataires.length === 0 || invalides.length > 0}>
            <Send className="size-4" /> {actions.envoyer.isPending ? 'Envoi…' : `Envoyer à ${destinataires.length} destinataire(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Bibliothèque des rapports d'exécution budgétaire (cahier des charges du tableau de bord, § 11 et 12) et génération à la demande. */
export function Rapports() {
  const session = useSession();
  const peutViser = (session?.utilisateur.profils ?? []).some((p) => ['DIRECTEUR_FINANCIER', 'ADMINISTRATEUR'].includes(p));
  const peutValider = (session?.utilisateur.profils ?? []).some((p) => ['DIRECTEUR_GENERAL', 'ADMINISTRATEUR'].includes(p));
  const actionsCloture = useActionsCommentairesCloture();
  const valider = async (r: ResumeRapport) => {
    try {
      const v = await actionsCloture.validerRapport.mutateAsync(r.id);
      toast.success(`Rapport annuel ${v.numero} validé : sa diffusion se déclenche depuis le bouton Envoyer.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation impossible.');
    }
  };
  const viser = async (r: ResumeRapport) => {
    try {
      const v = await actionsCloture.viser.mutateAsync(r.id);
      toast.success(v.etat === 'A_VALIDER' ? `Rapport annuel ${v.numero} visé : il attend la validation du Directeur Général.` : `Rapport ${v.numero} visé${v.nombreEnvois ? ' et envoyé au DG, aux DGA et aux directeurs' : ''}.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Visa impossible.');
    }
  };
  const perimetres = usePerimetresRapport();
  const { data, isLoading, error } = useRapports();
  const [dialogue, setDialogue] = useState(false);
  const [aEnvoyer, setAEnvoyer] = useState<ResumeRapport | null>(null);
  const autorise = Boolean(perimetres.data && (perimetres.data.fonds || perimetres.data.structures.length || perimetres.data.services.length));
  return (
    <div className="space-y-6">
      <PageHeader
        titre="Rapports d'exécution budgétaire"
        sousTitre="Bibliothèque des rapports générés (archivés, numérotés, non modifiables, en PDF et en Excel) ; rapports mensuels automatiques le 3e jour ouvré du mois suivant, trimestriels et annuels selon le calendrier ; rapport à la demande sur une période et un périmètre."
        actions={
          autorise && (
            <>
              <Button variant="outline" asChild>
                <Link to="/rapports/commentaires">
                  <MessageSquareText className="size-4" /> Commentaires de clôture
                </Link>
              </Button>
              <Button onClick={() => setDialogue(true)}>
                <FileBarChart2 className="size-4" /> Générer un rapport
              </Button>
            </>
          )
        }
      />
      {perimetres.data && !autorise && <Vide message="Les rapports d'exécution sont réservés à la Direction, au contrôle de gestion, aux directeurs et aux chefs de service sur leur périmètre." />}
      {isLoading ? (
        <Chargement />
      ) : error || !data ? (
        <ErreurBloc erreur={error} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Rapports générés ({data.length})</CardTitle>
            <CardDescription>Chaque rapport porte ses indicateurs clés figés à la génération ; la consultation est journalisée.</CardDescription>
          </CardHeader>
          <CardContent>
            {data.length === 0 ? (
              <Vide message="Aucun rapport généré." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Période</TableHead>
                    <TableHead>Périmètre</TableHead>
                    <TableHead className="hidden text-right md:table-cell">Budget révisé</TableHead>
                    <TableHead className="text-right">Payé</TableHead>
                    <TableHead className="text-right">Exécution</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Réalisé / prévu</TableHead>
                    <TableHead className="hidden lg:table-cell">Alertes</TableHead>
                    <TableHead className="hidden md:table-cell">Généré</TableHead>
                    <TableHead className="hidden lg:table-cell">Envoi</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <div className="font-medium">{r.numero}</div>
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {r.automatique && <Badge variant="outline">{r.type === 'ANNUEL' ? 'annuel' : r.filtres.type === 'TRIMESTRE' ? 'trimestriel' : 'mensuel'} automatique</Badge>}
                          {r.etat === 'BROUILLON' && (
                            <Badge variant="secondary" title="Rapport de la Direction Générale produit avant les explications des directeurs ; la version définitive suit la date limite des commentaires de clôture.">
                              brouillon
                            </Badge>
                          )}
                          {r.etat === 'A_VISER' && (
                            <Badge variant="destructive" title="Rapport trimestriel de la Direction Générale en attente du visa du Directeur financier ; le visa le rend définitif et déclenche l'envoi.">
                              visa DF attendu
                            </Badge>
                          )}
                          {r.etat === 'A_VALIDER' && (
                            <Badge variant="destructive" title="Rapport annuel du Fonds visé par le Directeur financier ; la validation du Directeur Général est obligatoire avant sa diffusion.">
                              validation DG attendue
                            </Badge>
                          )}
                          {r.viseParNom && (
                            <Badge variant="default" title={`Visé par ${r.viseParNom} le ${dateHeureFr(r.viseLe)}`}>
                              visé DF
                            </Badge>
                          )}
                          {r.valideParNom && (
                            <Badge variant="default" title={`Validé par ${r.valideParNom} le ${dateHeureFr(r.valideLe)}`}>
                              validé DG
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.periode}
                        {r.filtres.comparaison !== 'AUCUNE' && <div className="text-xs text-muted-foreground">avec comparaison {r.filtres.comparaison === 'N-1' ? 'N-1' : 'T/T-1'}</div>}
                      </TableCell>
                      <TableCell>{r.libellePerimetre}</TableCell>
                      <TableCell className="num hidden text-right md:table-cell">{montant(r.synthese.budgetRevise)}</TableCell>
                      <TableCell className="num text-right">{montant(r.synthese.paye)}</TableCell>
                      <TableCell className="num text-right">{pct(r.synthese.tauxExecution)}</TableCell>
                      <TableCell className="num hidden text-right lg:table-cell">{pct(r.synthese.realisePrevu)}</TableCell>
                      <TableCell className="hidden lg:table-cell">{r.synthese.lignesEnAlerte ? <Badge variant="destructive">{r.synthese.lignesEnAlerte} ligne(s)</Badge> : <span className="text-xs text-muted-foreground">aucune</span>}</TableCell>
                      <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                        {dateHeureFr(r.createdAt)}
                        <div>{r.genereParNom}</div>
                      </TableCell>
                      <TableCell className="hidden text-xs lg:table-cell">
                        {r.nombreEnvois ? (
                          <>
                            <Badge variant="secondary">envoyé ×{r.nombreEnvois}</Badge>
                            <div className="text-muted-foreground">{r.envoyeLe ? dateHeureFr(r.envoyeLe) : ''}</div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">non envoyé</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <BoutonPdf chemin={`/rapports/${r.id}/pdf`} nom={r.nomFichier} taille="icon" variant="ghost" />
                          {r.nomFichierExcel && <BoutonExcel chemin={`/rapports/${r.id}/xlsx`} nom={r.nomFichierExcel} libelle="" taille="sm" />}
                          {r.etat === 'A_VISER' && peutViser ? (
                            <Button size="sm" onClick={() => viser(r)} disabled={actionsCloture.viser.isPending} title={r.type === 'ANNUEL' ? 'Viser le rapport annuel : il passe à la validation du Directeur Général' : 'Viser le rapport : il devient définitif et part au DG, aux DGA et à tous les directeurs'}>
                              <FileCheck2 className="size-4" /> {r.type === 'ANNUEL' ? 'Viser' : 'Viser et envoyer'}
                            </Button>
                          ) : r.etat === 'A_VALIDER' && peutValider ? (
                            <Button size="sm" onClick={() => valider(r)} disabled={actionsCloture.validerRapport.isPending} title="Valider le rapport annuel : il devient définitif ; sa diffusion se déclenche ensuite depuis le bouton Envoyer">
                              <FileCheck2 className="size-4" /> Valider
                            </Button>
                          ) : r.etat !== 'DEFINITIF' ? null : (
                            <Button size="icon" variant="ghost" onClick={() => setAEnvoyer(r)} title={r.nombreEnvois ? 'Renvoyer par courriel' : 'Envoyer par courriel'} aria-label={`${r.nombreEnvois ? 'Renvoyer' : 'Envoyer'} ${r.numero}`}>
                              <Send className="size-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
      {dialogue && <DialogueGeneration ouvert onOpenChange={(o) => !o && setDialogue(false)} />}
      {aEnvoyer && <DialogueEnvoi key={aEnvoyer.id} rapport={aEnvoyer} onOpenChange={(o) => !o && setAEnvoyer(null)} />}
    </div>
  );
}
